import { Box, BoxProps, SxProps, Theme } from "@mui/material";
import { PADDING } from "../global-config/BaseConfig";

/**
 * Definition of common types of Boxes used across the app
 *
 * They all extend from a common style to ensure consistency
 *
 * Individual style modification is still possible via the SX prop and props will be passed
 *
 * It is preferred to define custom prop and handle the style modification internally if it is
 * not intended as a spot fix
 */

/**
 * Define props and a handler for common use cases (sx)
 */
type SxOverrides = {
	noPad?: boolean;
};

const SxOverrideHandler = (sxDefault: SxProps<Theme>, sxOverrides?: SxOverrides) => {
	if (!sxOverrides) {
		return sxDefault;
	}

	let sx = { ...sxDefault };

	sx = sxOverrides?.noPad ? { ...sx, padding: "0px" } : sx;

	return sx;
};

/**
 * Additional props for functions internal logic
 */
type InternalLogic = {
	dummyLogic?: boolean;
};

/**
 * Define the type for all generic box props
 */
type GenericBoxProps = SxOverrides & BoxProps & InternalLogic;

/* MUI Box with a flex display */
const sxFlexBox: SxProps<Theme> = {
	display: "flex",
	padding: PADDING,
	width: "100%",
	height: "auto",
	boxSizing: "border-box",
};
export const FlexBox = (props: GenericBoxProps) => {
	const { noPad, children, sx, ...rest } = props;
	const sxDefault = SxOverrideHandler(sxFlexBox, { noPad });

	return (
		<Box sx={{ ...sxDefault, ...sx }} {...rest}>
			{children}
		</Box>
	);
};

/* MUI Box with a centered flex display */
const sxFlexBoxCentered: SxProps<Theme> = {
	...sxFlexBox,

	justifyContent: "center",
	alignItems: "center",
};
export const FlexBoxCentered = (props: GenericBoxProps) => {
	const { noPad, children, sx, ...rest } = props;
	const sxDefault = SxOverrideHandler(sxFlexBoxCentered, { noPad });

	return (
		<Box sx={{ ...sxDefault, ...sx }} {...rest}>
			{children}
		</Box>
	);
};

/* MUI Box with a vertical flex display */
const sxFlexBoxVertical: SxProps<Theme> = {
	...sxFlexBox,

	flexDirection: "column",
};
export const FlexBoxVertical = (props: GenericBoxProps) => {
	const { noPad, children, sx, ...rest } = props;
	const sxDefault = SxOverrideHandler(sxFlexBoxVertical, { noPad });

	return (
		<Box sx={{ ...sxDefault, ...sx }} {...rest}>
			{children}
		</Box>
	);
};

/* MUI Box with a centered vertical flex display */
const sxFlexBoxVerticalCentered: SxProps<Theme> = {
	...sxFlexBoxVertical,

	justifyContent: "center",
	alignItems: "center",
};
export const FlexBoxVerticalCentered = (props: GenericBoxProps) => {
	const { noPad, children, sx, ...rest } = props;
	const sxDefault = SxOverrideHandler(sxFlexBoxVerticalCentered, { noPad });

	return (
		<Box sx={{ ...sxDefault, ...sx }} {...rest}>
			{children}
		</Box>
	);
};
